// src/components/layout/Navigation/ThemeToggle.tsx
/**
 * Light/dark switch in the app bar. Sun and moon are the convention everyone already knows for
 * this (Style Guide §8's short list), and the tooltip names the theme it switches to. The choice
 * is per browser and applies by a reload: the colour roles are fixed when tokens.ts loads.
 */
import { IconButton, Tooltip } from '@mui/material';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import { alpha, applyThemeChoice, colors, darkThemeAvailable, themeChoice } from '@/styles/base/tokens';

interface ThemeToggleProps {
    small?: boolean;
}

export function ThemeToggle({ small = false }: ThemeToggleProps) {
    if (!darkThemeAvailable) return null;

    const isDark = themeChoice === 'dark';
    const label = isDark ? 'Switch to light theme' : 'Switch to dark theme';
    const Icon = isDark ? LightModeOutlinedIcon : DarkModeOutlinedIcon;

    return (
        <Tooltip title={label} arrow placement="bottom">
            <IconButton
                aria-label={label}
                onClick={() => applyThemeChoice(isDark ? 'light' : 'dark')}
                size="medium"
                sx={{
                    color: colors.appBarText,
                    opacity: 0.9,
                    mr: 1,
                    '&:hover': {
                        backgroundColor: alpha(colors.appBarText, 0.08),
                        opacity: 1,
                    },
                }}
            >
                <Icon fontSize={small ? 'small' : 'medium'} />
            </IconButton>
        </Tooltip>
    );
}
